<script lang="ts">
  import TableBubbleMenu from "./TableBubbleMenu.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import ToolTip from "$components/+blocks/Tooltip/Tooltip.svelte";
  import { NodeViewWrapper, NodeViewContent } from "svelte-tiptap";
  import { Dots } from "$components/+blocks/OVIcon";
  import type { NodeViewProps } from "@tiptap/core";

  export let editor: NodeViewProps["editor"];
  export let isEditable: boolean;
  export let getPos: NodeViewProps["getPos"];

  $$restProps; // consume unused tiptap props

  // element refs for tippy
  let columnMenu: HTMLDivElement;
  let rowMenu: HTMLDivElement;

  // track hover states for col/row menu button visibility
  // when the mouse enters the cell, show the menus - these fade out after 1 second (so it doesn't obstruct the cell if the user types in it)
  // if the mouse is hovering the menu buttons, they will stay visible (no timeout)

  let isHovered = false;
  let isFaded = false;
  let timeout: NodeJS.Timeout;

  function handleMouseEnter(withTimeout: boolean) {
    isHovered = true;

    if (withTimeout) {
      // After 1 second, set isFaded to true
      timeout = setTimeout(() => {
        isFaded = true;
      }, 1000);
    } else {
      clearTimeout(timeout);
    }
  }

  function handleMouseLeave() {
    clearTimeout(timeout);

    isHovered = false;
    isFaded = false;
  }
</script>

<!-- a named group is needed here (group/cell) so it doesn't conflict with the group for the gutter menu higher up -->
<NodeViewWrapper class="group/cell relative h-full w-full">
  <div
    class="h-full px-1 py-2 whitespace-normal"
    on:mouseenter={() => handleMouseEnter(true)}
    on:mouseleave={handleMouseLeave}
    role="group"
  >
    <NodeViewContent />
  </div>

  {#if isEditable}
    <!-- COLUMN MENU -->
    <div
      class="absolute -top-2.5 right-1/2 z-[10] hidden h-full translate-x-1/2 -translate-y-[1px] group-hover/cell:inline-block"
      contenteditable="false"
      style:opacity={isHovered && !isFaded ? "1" : "0"}
      style:transform={`translateY(${isHovered && !isFaded ? "0" : "-100%"})`}
      style:transition="opacity 0.5s, transform 0.5s"
      on:mouseenter={() => handleMouseEnter(false)}
      on:mouseleave={handleMouseLeave}
      role="group"
    >
      <ToolTip interactive headless appendToBody clickToTrigger>
        <svelte:fragment slot="tip">
          <TableBubbleMenu
            bind:element={columnMenu}
            {editor}
            updatePosition={() => editor.commands.setNodeSelection(getPos())}
            type="column"
          />
        </svelte:fragment>

        <span class="rounded-[5px] bg-white dark:bg-neutral-900">
          <IconButton variant="primary" size="small" icon={Dots} a11yLabel="Add / delete column" />
        </span>
      </ToolTip>
    </div>

    <!-- ROW MENU -->
    <div
      class="absolute top-2 left-1 z-[10] hidden h-full group-hover/cell:inline-block"
      contenteditable="false"
      style="opacity: {isHovered && !isFaded ? '1' : '0'}; transform: translateX({isHovered &&
      !isFaded
        ? '0'
        : '-100%'}); transition: opacity 0.5s, transform 0.5s;"
      on:mouseenter={() => handleMouseEnter(false)}
      on:mouseleave={handleMouseLeave}
      role="group"
    >
      <ToolTip placement="left" interactive headless appendToBody clickToTrigger>
        <svelte:fragment slot="tip">
          <TableBubbleMenu
            bind:element={rowMenu}
            {editor}
            updatePosition={() => editor.commands.setNodeSelection(getPos())}
            type="row"
          />
        </svelte:fragment>
        <span class="rounded-[5px] bg-white dark:bg-neutral-900">
          <IconButton variant="primary" size="small" icon={Dots} a11yLabel="Add / delete row" />
        </span>
      </ToolTip>
    </div>
  {/if}
</NodeViewWrapper>
